import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Calendar, CheckCircle2, Clock, Bell,
  ChevronRight, ExternalLink, XCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ExpiringDocument {
  id: string;
  document_type: string;
  expiration_date: string;
  days_until_expiration: number;
  status: string;
  document_url?: string;
}

interface ComplianceExpirationWidgetProps {
  compact?: boolean;
  daysAhead?: number;
}

export function ComplianceExpirationWidget({
  compact = false,
  daysAhead = 90
}: ComplianceExpirationWidgetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expiringDocs, setExpiringDocs] = useState<ExpiringDocument[]>([]);
  const [expiredCount, setExpiredCount] = useState(0);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);

  useEffect(() => {
    if (user?.id) {
      loadExpiringDocuments();
    }
  }, [user?.id]);

  const loadExpiringDocuments = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Fetch documents expiring within the specified days
      const { data, error } = await supabase
        .from('compliance_documents')
        .select('id, document_type, expiration_date, status, document_url')
        .eq('profile_id', user.id)
        .not('expiration_date', 'is', null)
        .order('expiration_date', { ascending: true });

      if (error) throw error;

      const now = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);

      const processedDocs: ExpiringDocument[] = (data || [])
        .map(doc => {
          const expDate = new Date(doc.expiration_date);
          const daysUntil = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          return {
            ...doc,
            days_until_expiration: daysUntil,
            status: daysUntil < 0 ? 'expired' : daysUntil <= 30 ? 'expiring_soon' : 'active'
          };
        })
        .filter(doc => doc.days_until_expiration <= daysAhead);

      setExpiringDocs(processedDocs);
      setExpiredCount(processedDocs.filter(d => d.status === 'expired').length);
      setExpiringSoonCount(processedDocs.filter(d => d.status === 'expiring_soon').length);
    } catch (error) {
      console.error('Error loading expiring documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string, daysUntil: number) => {
    if (status === 'expired' || daysUntil < 0) return 'text-urgent bg-urgent-bg border-urgent-border';
    if (daysUntil <= 30) return 'text-fit-moderate bg-orange-50 border-orange-200';
    if (daysUntil <= 60) return 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border';
    return 'text-success bg-success-bg border-success-border';
  };

  const getStatusIcon = (status: string, daysUntil: number) => {
    if (status === 'expired' || daysUntil < 0) return <XCircle className="h-4 w-4 text-urgent" />;
    if (daysUntil <= 30) return <AlertTriangle className="h-4 w-4 text-fit-moderate" />;
    if (daysUntil <= 60) return <Clock className="h-4 w-4 text-fit-moderate" />;
    return <CheckCircle2 className="h-4 w-4 text-success" />;
  };

  const formatDaysUntil = (days: number) => {
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `Expires in ${days} days`;
  };

  if (loading) {
    return (
      <div className="bg-bw-surface rounded-lg shadow p-4 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  // Compact mode for sidebar/dashboard
  if (compact) {
    const hasIssues = expiredCount > 0 || expiringSoonCount > 0;

    return (
      <button
        onClick={() => navigate('/profile#compliance')}
        className={`w-full text-left p-3 rounded-lg border transition-colors ${
          expiredCount > 0
            ? 'bg-urgent-bg border-urgent-border hover:bg-urgent-bg'
            : expiringSoonCount > 0
            ? 'bg-fit-moderate-bg border-fit-moderate-border hover:bg-fit-moderate-bg'
            : 'bg-success-bg border-success-border hover:bg-success-bg'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {expiredCount > 0 ? (
              <XCircle className="h-5 w-5 text-urgent mr-2" />
            ) : expiringSoonCount > 0 ? (
              <AlertTriangle className="h-5 w-5 text-fit-moderate mr-2" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-success mr-2" />
            )}
            <div>
              <p className="text-sm font-medium text-bw-ink">Compliance Status</p>
              <p className={`text-xs ${
                expiredCount > 0 ? 'text-urgent' :
                expiringSoonCount > 0 ? 'text-fit-moderate' : 'text-success'
              }`}>
                {expiredCount > 0
                  ? `${expiredCount} expired`
                  : expiringSoonCount > 0
                  ? `${expiringSoonCount} expiring soon`
                  : 'All current'}
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-bw-muted" />
        </div>
      </button>
    );
  }

  // Full widget mode
  return (
    <div className="bg-bw-surface rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-bw-surface-subtle">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <Bell className="h-5 w-5 mr-2 text-bw-primary" />
            Certification Expiration Alerts
          </h3>
          <div className="flex items-center space-x-2">
            {expiredCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-urgent-bg text-urgent rounded-full">
                {expiredCount} expired
              </span>
            )}
            {expiringSoonCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-fit-moderate-bg text-fit-moderate rounded-full">
                {expiringSoonCount} expiring
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {expiringDocs.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-3" />
            <p className="text-bw-muted">All certifications are current</p>
            <p className="text-sm text-bw-muted">No expirations within the next {daysAhead} days</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expiringDocs.map(doc => (
              <div
                key={doc.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor(doc.status, doc.days_until_expiration)}`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {getStatusIcon(doc.status, doc.days_until_expiration)}
                  <div className="ml-3 min-w-0">
                    <p className="text-sm font-medium text-bw-ink truncate">
                      {doc.document_type}
                    </p>
                    <p className="text-xs text-bw-muted flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDaysUntil(doc.days_until_expiration)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {doc.document_url && (
                    <a
                      href={doc.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-bw-muted hover:text-bw-muted"
                      title="View document"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() => navigate('/profile#compliance')}
                    className="text-xs text-bw-primary hover:text-bw-primary font-medium"
                  >
                    Update
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {expiringDocs.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-start text-sm">
              <AlertTriangle className="h-4 w-4 text-fit-moderate mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-bw-muted">
                <p className="font-medium">Action Required</p>
                <p className="text-xs mt-1">
                  {expiredCount > 0 && `Renew ${expiredCount} expired certification(s) immediately. `}
                  {expiringSoonCount > 0 && `Plan renewal for ${expiringSoonCount} certification(s) expiring soon.`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-bw-surface-subtle border-t">
        <button
          onClick={() => navigate('/profile#compliance')}
          className="w-full text-center text-sm text-bw-primary hover:text-bw-primary font-medium"
        >
          Manage Compliance Documents
        </button>
      </div>
    </div>
  );
}
